import { Control, Controller } from 'react-hook-form';
import { useState } from 'react';

import { DecomPolicyFormValues } from '../../decom-policy-form-layout.tsx';
import VaultTooltipWithLink from './vault-tooltip-with-link.tsx';
import { AttendeeResponse } from '../../../../../../../types';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { useTheme } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import FormHelperText from '@mui/material/FormHelperText';
import ExpandMore from '@mui/icons-material/ExpandMore';

export interface VaultCalendarAdvancedOptionsProps {
  day: number;
  idx: number;
  control: Control<DecomPolicyFormValues>;
}

export default function VaultCalendarAdvancedOptions({
  day,
  idx,
  control,
}: VaultCalendarAdvancedOptionsProps) {
  const theme = useTheme();
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);

  const handleToggleResponseStatus = (
    status: string,
    selected: string[],
    onChange: (value: string[]) => void
  ) => {
    if (selected.includes(status)) {
      onChange(selected.filter((s) => s !== status));
    } else {
      onChange([...selected, status]);
    }
  };

  return (
    <Accordion
      elevation={0}
      disableGutters
      expanded={isAdvancedExpanded}
      onChange={() => setIsAdvancedExpanded(!isAdvancedExpanded)}
      sx={{
        '&:before': { display: 'none' },
        background: 'transparent',
        boxShadow: 'none',
        border: 'none',
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMore sx={{ color: theme.palette.colors?.primary?.[600] }} />
        }
        sx={{
          px: 0,
          minHeight: 'auto',
          width: 'fit-content',
          backgroundColor: 'transparent !important',
          '& .MuiAccordionSummary-content': { my: 0.5, flexGrow: 0, mr: 1 },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: theme.palette.colors?.primary?.[600],
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          {isAdvancedExpanded
            ? 'Hide Advanced Options'
            : 'Show Advanced Options'}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: 0,
          pt: 1,
          pb: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, fontWeight: 600 }}
            gutterBottom
          >
            Who (optional)
          </Typography>
          <Controller
            name={`actions.${day}.actions.${idx}.vaultPeopleQuery`}
            control={control}
            render={({ field }) => (
              <Box>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={field.value || []}
                  onChange={(_, newValue) => {
                    field.onChange(newValue);
                  }}
                  renderTags={() => null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Enter guests, creators, or organizers"
                      size="small"
                      fullWidth
                      variant="standard"
                    />
                  )}
                />
                {field.value && field.value.length > 0 && (
                  <Stack
                    direction="row"
                    gap={1}
                    flexWrap="wrap"
                    sx={{ mt: 1, mb: 0.5 }}
                  >
                    {field.value.map((val: string, index: number) => (
                      <Chip
                        key={`${val}-${index}`}
                        variant="outlined"
                        label={val}
                        size="small"
                        onDelete={() => {
                          const newVals = (field.value || []).filter(
                            (_: string, i: number) => i !== index
                          );
                          field.onChange(newVals);
                        }}
                      />
                    ))}
                  </Stack>
                )}
                <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                  Press Enter to separate multiple users. Example:
                  user@domain.com
                </FormHelperText>
              </Box>
            )}
          />
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, fontWeight: 600 }}
            gutterBottom
          >
            Where (optional)
          </Typography>
          <Controller
            name={`actions.${day}.actions.${idx}.vaultLocationQuery`}
            control={control}
            render={({ field }) => (
              <Box>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={field.value || []}
                  onChange={(_, newValue) => {
                    field.onChange(newValue);
                  }}
                  renderTags={() => null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Enter location or rooms"
                      size="small"
                      fullWidth
                      variant="standard"
                    />
                  )}
                />
                {field.value && field.value.length > 0 && (
                  <Stack
                    direction="row"
                    gap={1}
                    flexWrap="wrap"
                    sx={{ mt: 1, mb: 0.5 }}
                  >
                    {field.value.map((val: string, index: number) => (
                      <Chip
                        key={`${val}-${index}`}
                        variant="outlined"
                        label={val}
                        size="small"
                        onDelete={() => {
                          const newVals = (field.value || []).filter(
                            (_: string, i: number) => i !== index
                          );
                          field.onChange(newVals);
                        }}
                      />
                    ))}
                  </Stack>
                )}
                <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                  Press Enter to separate multiple locations or rooms
                </FormHelperText>
              </Box>
            )}
          />
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, fontWeight: 600 }}
            gutterBottom
          >
            Doesn't have (optional)
          </Typography>
          <Controller
            name={`actions.${day}.actions.${idx}.vaultMinusWords`}
            control={control}
            render={({ field }) => (
              <Box>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={field.value || []}
                  onChange={(_, newValue) => {
                    field.onChange(newValue);
                  }}
                  renderTags={() => null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Keywords not contained in the title, description, guests, locations, or rooms"
                      size="small"
                      fullWidth
                      variant="standard"
                    />
                  )}
                />
                {field.value && field.value.length > 0 && (
                  <Stack
                    direction="row"
                    gap={1}
                    flexWrap="wrap"
                    sx={{ mt: 1, mb: 0.5 }}
                  >
                    {field.value.map((val: string, index: number) => (
                      <Chip
                        key={`${val}-${index}`}
                        variant="outlined"
                        label={val}
                        size="small"
                        onDelete={() => {
                          const newVals = (field.value || []).filter(
                            (_: string, i: number) => i !== index
                          );
                          field.onChange(newVals);
                        }}
                      />
                    ))}
                  </Stack>
                )}
                <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                  Press Enter to separate multiple terms
                </FormHelperText>
              </Box>
            )}
          />
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontWeight: 600,
            }}
            gutterBottom
          >
            Response status (optional)
            <VaultTooltipWithLink preset="calendarResponseStatus" />
          </Typography>
          <Controller
            name={`actions.${day}.actions.${idx}.vaultResponseStatus`}
            control={control}
            render={({ field }) => {
              const selected = field.value || [];
              return (
                <Stack direction="row" gap={3} flexWrap="wrap">
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.includes(AttendeeResponse.ACCEPTED)}
                        onChange={() =>
                          handleToggleResponseStatus(
                            AttendeeResponse.ACCEPTED,
                            selected,
                            field.onChange
                          )
                        }
                      />
                    }
                    label={<Typography variant="body2">Yes</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.includes(AttendeeResponse.DECLINED)}
                        onChange={() =>
                          handleToggleResponseStatus(
                            AttendeeResponse.DECLINED,
                            selected,
                            field.onChange
                          )
                        }
                      />
                    }
                    label={<Typography variant="body2">No</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.includes(AttendeeResponse.TENTATIVE)}
                        onChange={() =>
                          handleToggleResponseStatus(
                            AttendeeResponse.TENTATIVE,
                            selected,
                            field.onChange
                          )
                        }
                      />
                    }
                    label={<Typography variant="body2">Maybe</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.includes(
                          AttendeeResponse.NEEDS_ACTION
                        )}
                        onChange={() =>
                          handleToggleResponseStatus(
                            AttendeeResponse.NEEDS_ACTION,
                            selected,
                            field.onChange
                          )
                        }
                      />
                    }
                    label={<Typography variant="body2">No response</Typography>}
                  />
                </Stack>
              );
            }}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
